import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface UsePDFPageVisibilityResult {
  visiblePages: Set<number>;
  currentPage: number;
  pageRefs: (pageNumber: number) => (el: HTMLElement | null) => void;
}

export function usePDFPageVisibility(
  scrollContainerRef: RefObject<HTMLElement | null>,
  numPages: number,
  rootMargin = "200px 0px"
): UsePDFPageVisibilityResult {
  const [visiblePages, setVisiblePages] = useState<Set<number>>(() => new Set([1]));
  const [currentPage, setCurrentPage] = useState(1);
  const pageElements = useRef<Map<number, HTMLElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const pageRefs = useCallback(
    (pageNumber: number) => (el: HTMLElement | null) => {
      if (el) {
        pageElements.current.set(pageNumber, el);
      } else {
        pageElements.current.delete(pageNumber);
      }

      if (observerRef.current) {
        if (el) {
          observerRef.current.observe(el);
        }
      }
    },
    []
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || numPages <= 0) return;

    const visibleSet = new Set<number>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageNum = Number((entry.target as HTMLElement).dataset.page);
          if (isNaN(pageNum)) return;

          if (entry.isIntersecting) {
            visibleSet.add(pageNum);
          } else {
            visibleSet.delete(pageNum);
          }
        });

        setVisiblePages(new Set(visibleSet));

        if (visibleSet.size > 0) {
          const sorted = Array.from(visibleSet).sort((a, b) => a - b);
          setCurrentPage(sorted[0]);
        }
      },
      {
        root: container,
        rootMargin,
        threshold: 0
      }
    );

    observerRef.current = observer;

    pageElements.current.forEach((el) => {
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [scrollContainerRef, numPages, rootMargin]);

  return { visiblePages, currentPage, pageRefs };
}
